import { receiveStatus } from "./receiveStatus";
import { receiveType } from "./receiveTypeMapping";
import { v4 as uuidv4 } from "uuid";
import { ReceiveTransferDetailResponse } from "../common/api/Models/ReceiveTransferDetailResponse";
import { Receive } from "../common/Receive";
import { InvoiceLineItem } from "../common/InvoiceLineItem";
import { StaffProfile } from "../infrastructure/auth";
import { Transfer, TransferReceiveVariance } from "../common/Transfer";
import { transferStatus } from "./transferStatus";
import { transferSourceSystem } from "./transferSourceSystem";
import { Product } from "../common/Product";

/**
 * Interface for quantity difference items displayed in unverified modal
 */
export interface UnverifiedQuantityDifference {
	sku: string;
	approvedQuantity: number;
	receivedQuantity: number;
	description: string;
}

const createReceiveLineItemFromInvoiceLineItem = (item: InvoiceLineItem) => {
	return {
		sku: item.sku,
		model: item.model,
		description: item.description,
		barcode: item.barcode,
		receivedQuantity: item.received,
		quantityAlreadyReceived: item.quantityAlreadyReceived ?? 0,
		originalReceivedQuantity: item.originalReceived,
		modifiedDate: item.modifiedTimestamp.toISOString(),
		invoicedQuantity: item.invoiced,
		inventory: item.inventory,
		inboundLocation: item.inboundLocation,
		hasLabel: item.hasLabel,
		outstandingQuantity: item.invoiced - item.received,
		damagedQuantity: 0,
		unitCostEx: 0,
		unitCostInc: 0,
		unitCostTax: 0,
		unitDiscountCostInc: 0,
		lineTotalCostTax: 0,
		lineDiscountCostInc: 0,
		notes: "",
		isDiscounted: false,
		lineTotalCostEx: 0,
		lineTotalCostInc: 0,
		taxPercentage: 0,
		taxAmountEx: 0,
		taxAmountInc: 0,
		taxType: "",
		taxCode: "",
		taxDescription: "",
		taxRate: 0,
	};
};

const createOrUpdateTransferReceive = (
	transferDetails: ReceiveTransferDetailResponse,
	receive: Receive | null,
	lineItems: InvoiceLineItem[],
	graphProfile: StaffProfile,
	receivedDate: Date,
	selectedCommercialAccount: string,
	notes: string
): Receive => {
	const { staffId, displayName } = graphProfile;
	const transfer = transferDetails.transfer;

	const receiveLineItems = lineItems.map(createReceiveLineItemFromInvoiceLineItem);

	if (receive) {
		receive = {
			...receive,
			receivedDate: receivedDate.toISOString(),
			totalReceivedQuantity: receiveLineItems.reduce((t, { receivedQuantity }) => t + receivedQuantity, 0),
			modifiedBy: displayName,
			modifiedById: staffId,
			receiveLineItems: receiveLineItems,
			status:
				receive.status === receiveStatus.arrived || receive.status === receiveStatus.open
					? receiveStatus.inProgress
					: receive.status,
			inventoryStatus: selectedCommercialAccount,
			notes: notes,
			transferRequestId: transfer.transferRequestId,
		};
	} else {
		// new receive
		receive = {
			id: uuidv4(),
			supplierId: "",
			supplierName: "",
			purchaseOrderNumber: "",
			invoiceNumber: "",
			isEdi: false,
			receiveLineItems: receiveLineItems,
			status: receiveStatus.inProgress,
			createdBy: displayName,
			createdById: staffId,
			modifiedBy: displayName,
			modifiedById: staffId,
			createdTimestamp: new Date().toISOString(),
			receivedDate: receivedDate.toISOString(),
			totalReceivedQuantity: receiveLineItems.reduce((t, { receivedQuantity }) => t + receivedQuantity, 0),
			locationId: transfer.toLocation.locationId,
			organisationId: "JBH",
			receiveType: receiveType.transfer,
			invoiceDate: transfer.dateCreated,
			totalInvoicedQuantity:
				transfer.sourceSystem.toLowerCase() === transferSourceSystem.Intranet ||
				transfer.sourceSystem.toLowerCase() === transferSourceSystem.ReceivingApp
					? transfer.totalApprovedQuantity
					: transfer.totalInTransitQuantity,
			sourceId: transfer.id,
			inventoryStatus: selectedCommercialAccount,
			timestamp: new Date().toISOString(),
			totalInvoicedFreightEx: 0,
			totalInvoicedFreightInc: 0,
			totalInvoicedFreightTax: 0,
			notes: notes,
			receiver: "",
			receiverId: "",
			receiptAttemptCount: 0,
			statusHistory: [],
			asnId: "",
			isPartReceived: false,
			transferRequestId: transfer.transferRequestId,
		};
	}
	return receive;
};

const getTransferReceiveVariances = (receive: Receive, transfer?: Transfer) => {
	if (!receive || !receive.receiveLineItems.length) return { overReceivedItems: [], underReceivedItems: [] };

	const fromLocation = transfer?.fromLocation;
	const toLocation = transfer?.toLocation;

	return receive.receiveLineItems.reduce(
		(acc, rli) => {
			const varianceQty = Math.abs(rli.invoicedQuantity - rli.receivedQuantity);
			if (varianceQty === 0) return acc;

			const variance: TransferReceiveVariance = {
				sku: rli.sku,
				qty: varianceQty,
				fromLocation: {
					locationName: fromLocation?.locationName ?? "",
					locationId: fromLocation?.locationId ?? 0,
				},
				toLocation: {
					locationName: toLocation?.locationName ?? "",
					locationId: toLocation?.locationId ?? 0,
				},
			};

			(rli.invoicedQuantity > rli.receivedQuantity ? acc.underReceivedItems : acc.overReceivedItems).push(variance);

			return acc;
		},
		{ overReceivedItems: [], underReceivedItems: [] } as {
			overReceivedItems: TransferReceiveVariance[];
			underReceivedItems: TransferReceiveVariance[];
		}
	);
};

const isTransferCompleted = (trnsfrStatus: string) => {
	return (
		trnsfrStatus === transferStatus.completed ||
		trnsfrStatus === transferStatus.completedWithVariances ||
		trnsfrStatus === transferStatus.completedManuallyRecievedInSK
	);
};

const isTransferReceivedDirectInHost = (rcvStatus: string, trnsfrStatus: string) => {
	return isTransferCompleted(trnsfrStatus) && rcvStatus === receiveStatus.inProgress;
};

const getCustomerOrderNumber = (references?: Record<string, string>) => {
	const key = Object.keys(references ?? {}).find((reference) => reference.toLowerCase() === "customerordernumber");
	const value = key ? references?.[key] : undefined;
	const trimmedValue = value?.trim();

	return trimmedValue || undefined;
};

/**
 * Calculates SKUs where received quantity differs from approved quantity.
 * Used to populate the unverified transfer finalize modal.
 *
 * @param receive - The receive record with line items
 * @param transfer - The transfer record with approved quantities
 * @param products - Array of products for descriptions
 * @returns Array of SKUs with quantity differences
 */
const getUnverifiedQuantityDifferences = (
	receive: Receive,
	transfer: Transfer,
	products?: Product[]
): UnverifiedQuantityDifference[] => {
	return receive.receiveLineItems
		.map((rli) => {
			const transferLineItem = transfer.lineItems.find((li) => li.sku === rli.sku);
			const product = products?.find((p) => p.id === rli.sku);

			return {
				sku: rli.sku,
				approvedQuantity: transferLineItem?.approvedQuantity ?? 0,
				receivedQuantity: rli.receivedQuantity,
				description: product?.description ?? "",
			};
		})
		.filter((item) => item.approvedQuantity !== item.receivedQuantity);
};

export {
	createReceiveLineItemFromInvoiceLineItem,
	createOrUpdateTransferReceive,
	getTransferReceiveVariances,
	isTransferCompleted,
	isTransferReceivedDirectInHost,
	getUnverifiedQuantityDifferences,
	getCustomerOrderNumber,
};
